import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Target, ExternalLink, Save, Check, Loader2, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import FeedbackButtons, { LEAD_REASONS } from "@/components/FeedbackButtons";
import { VerificationBadges } from "@/components/VerificationBadges";
import { ProspectExpandedDetails } from "./ProspectExpandedDetails";
import { ProspectOutreach } from "./ProspectOutreach";

const icpBadgeClass = (level) =>
  level === "high" ? "bg-green-500/20 border-green-500 text-green-400" : level === "medium" ? "bg-yellow-500/20 border-yellow-500 text-yellow-400" : "bg-red-500/20 border-red-500 text-red-400";

const IcpBadge = ({ icpMatch, idx, className = "" }) => icpMatch ? (
  <Badge variant="outline" className={`text-xs px-2 py-0.5 ${className} ${icpBadgeClass(icpMatch.matchLevel)}`} data-testid={`icp-score-${idx}`}>
    <Target className="h-3 w-3 mr-1" />
    {icpMatch.score}% ICP
  </Badge>
) : null;

const ProspectIdentity = ({ company, idx }) => (
  <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2 mb-1">
      {company.personName && <h4 className="text-white font-semibold">{company.personName}</h4>}
      <IcpBadge icpMatch={company.icpMatch} idx={idx} />
    </div>
    {company.personTitle && <p className="text-gray-400 text-sm">{company.personTitle}</p>}
    {company.companyName && <p className="text-[text-ism-teal] text-sm">at {company.companyName}</p>}
    {!company.personName && company.title && (
      <div className="flex items-center gap-2">
        <h4 className="text-white font-semibold text-sm">{company.title}</h4>
        <IcpBadge icpMatch={company.icpMatch} idx={idx} className="flex-shrink-0" />
      </div>
    )}
    {company.icpMatch?.issues?.length > 0 && (
      <div className="mt-1">
        {company.icpMatch.issues.map((issue) => <span key={issue} className="text-xs text-orange-400/70 mr-2">⚠️ {issue}</span>)}
      </div>
    )}
  </div>
);

const ProspectEvidence = ({ company }) => (
  <>
    {company.snippet && !company.whyGoodFit?.length && !company.businessIntel?.length && <p className="text-gray-400 text-sm mb-3">{company.snippet}</p>}
    {company.whyGoodFit?.length > 0 && (
      <div className="mb-3 p-3 rounded-lg bg-[#00A38A]/10 border border-[#00A38A]/20">
        <p className="text-xs text-[#00A38A] font-medium mb-2 uppercase tracking-wider">Why They're a Good Fit</p>
        <ul className="space-y-1">
          {company.whyGoodFit.map((reason) => (
            <li key={reason} className="flex items-start gap-2 text-sm text-gray-300"><span className="text-[#00A38A] mt-0.5">•</span><span>{reason}</span></li>
          ))}
        </ul>
      </div>
    )}
    {company.businessIntel?.length > 0 && (
      <div className="mb-3 p-3 rounded-lg bg-gray-800/30 border border-gray-700/50">
        <p className="text-xs text-gray-400 font-medium mb-2 uppercase tracking-wider">Business Intelligence</p>
        <div className="space-y-2">
          {company.businessIntel.slice(0, 3).map((intel, iIdx) => (
            <div key={intel.url || intel.text || iIdx} className="text-sm">
              <p className="text-gray-300 leading-relaxed">{intel.text}</p>
              {intel.url && (
                <a href={intel.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-[#00A38A] flex items-center gap-1 mt-1">
                  <span>Source: {intel.source}</span><ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    )}
    {company.sources?.length > 0 ? (
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {company.sources.slice(0, 4).map((source, sIdx) => (
          <a key={source.url || source.label || sIdx} href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#00A38A] transition-colors">
            <ExternalLink className="h-3 w-3" />{source.type}
          </a>
        ))}
      </div>
    ) : company.url && (
      <a href={company.url} target="_blank" rel="noopener noreferrer" className="text-[#00A38A] text-xs hover:underline flex items-center gap-1 mb-3">
        <ExternalLink className="h-3 w-3" />{company.url.length > 50 ? company.url.substring(0, 50) + "..." : company.url}
      </a>
    )}
  </>
);

export const ProspectCard = ({ company, idx, userId, actionId, isSaved, isSaving, isExpanded, isLoadingDetails, details, outreach, isGenerating, sendingEmail, copiedField,
  onSaveClick, onToggleExpand, onGenerateOutreach, onCopy, onSendEmail }) => (
  <div className="bg-white/5 rounded-lg border border-gray-800 overflow-hidden">
    <div className="p-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <ProspectIdentity company={company} idx={idx} />
        <div className="flex items-center gap-2 flex-shrink-0">
          <FeedbackButtons
            userId={userId}
            agentType="prospect"
            actionId={actionId}
            outputSummary={company.personName || company.title}
            context={{ leadIndex: idx, kind: "lead", companyName: company.companyName, personTitle: company.personTitle || null,
              sources: Array.isArray(company.sources) ? company.sources.filter((s) => typeof s === "string") : [], type: "prospect_lead" }}
            reasons={LEAD_REASONS}
            testIdPrefix={`prospect-${idx}`}
          />
          <Button size="sm" variant="outline" onClick={() => onSaveClick(company, idx)} disabled={isSaving || isSaved}
            className={isSaved ? "bg-green-600/20 border-green-600 text-green-400" : "border-gray-700 text-white hover:bg-white/10"} data-testid={`save-prospect-${idx}`}>
            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : isSaved ? <Check className="h-3 w-3" /> : <Save className="h-3 w-3" />}
            <span className="ml-1 text-xs">{isSaved ? "Saved" : "Save"}</span>
          </Button>
        </div>
      </div>

      <VerificationBadges prospect={company} idx={idx} />
      <ProspectEvidence company={company} />

      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => onToggleExpand(idx, company)} className="border-gray-700 text-gray-300 hover:bg-white/10" data-testid={`expand-details-${idx}`}>
          {isLoadingDetails ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : isExpanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          <span className="text-xs">{isExpanded ? "Less" : "More Details"}</span>
        </Button>
        {!outreach && (
          <Button size="sm" onClick={() => onGenerateOutreach(company, idx)} disabled={isGenerating} className="bg-[#00A38A] hover:bg-[#008570] text-white" data-testid={`generate-outreach-${idx}`}>
            {isGenerating ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Generating...</> : <><MessageSquare className="h-3 w-3 mr-1" />Generate Outreach</>}
          </Button>
        )}
      </div>
    </div>

    {isExpanded && <ProspectExpandedDetails company={company} details={details} isLoading={isLoadingDetails} />}
    {outreach && <ProspectOutreach idx={idx} outreach={outreach} company={company} copiedField={copiedField} sendingEmail={sendingEmail} onCopy={onCopy} onSendEmail={onSendEmail} />}
  </div>
);
